import type * as React from 'react';
import { useTranslation, Trans } from 'next-i18next';
import type { GetShopAvailQuery } from '../../generated/types';
import { InfoPopup } from '@dx-ui/osc-info-popup';
import { SkeletonLoader } from '@dx-ui/osc-skeleton-loader';
import Markdown from 'markdown-to-jsx';
import { Heading, Link, Text } from '@dx-ui/global-ui-prism';

type IProps = {
  roomRates?: NonNullable<NonNullable<GetShopAvailQuery['hotel']>['shopAvail']>['roomRates'];
  isNoAvailError: boolean;
  isAvailBellCurveError?: boolean;
  bookURL?: string;
};

const RoomsRates: React.FC<React.PropsWithChildren<IProps>> = ({
  isNoAvailError,
  roomRates,
  bookURL,
  isAvailBellCurveError,
}) => {
  const { t } = useTranslation();
  const visitBookingPageLink = (
    <Link url={bookURL ?? ''} data-testid="reservationURL">
      {t('buttonsAndLinks.visitBookingPage')}
    </Link>
  );
  const serviceChargeDetails = roomRates?.find(
    (roomRate) => !!roomRate.serviceChargeDetails
  )?.serviceChargeDetails;

  return (
    <div className="mt-8 flex flex-1 flex-col md:mx-11 md:mt-0">
      <div className="flex items-center gap-1">
        <Heading level={3} size="xs" className="font-bold">
          {t('heading.roomsNRates')}
        </Heading>
        <InfoPopup label={t('heading.roomsNRatesInfoLabel')}>
          <span className="flex w-64">{t('heading.roomsNRatesInformation')}</span>
        </InfoPopup>
      </div>
      {serviceChargeDetails && !isNoAvailError && roomRates.length > 0 ? (
        <div className="text--sm pt-1.5">
          <Markdown>{serviceChargeDetails}</Markdown>
        </div>
      ) : null}
      {roomRates && !isNoAvailError && roomRates.length === 0 && (
        <>
          <Text as="span" size="xs" className="py-1" aria-hidden="true">
            {t('textContent.roomsNRatesInfo')}
          </Text>
          <ul className="m-0 mt-4 list-none p-0" aria-hidden="true">
            <li className="mb-6">
              <SkeletonLoader className="h-6 w-full" variant="rectangular" />
            </li>
            <li className="mb-6">
              <SkeletonLoader className="h-6 w-full" variant="rectangular" />
            </li>
            <li className="mb-6">
              <SkeletonLoader className="h-6 w-full" variant="rectangular" />
            </li>
            <li className="m-0">
              <SkeletonLoader className="h-6 w-full" variant="rectangular" />
            </li>
          </ul>
        </>
      )}
      {roomRates && !isNoAvailError && roomRates.length > 0 && (
        <ul className="m-0 mt-4 list-none p-0">
          {roomRates.map((room) => (
            <li className="py-1" key={`${room.roomType?.roomTypeName}${room.rateAmountFmt}`}>
              <span>{`${room?.roomType?.roomTypeName || ''} - `}</span>
              <span className="font-bold">{room.rateAmountFmt}</span>
            </li>
          ))}
        </ul>
      )}
      {isNoAvailError && !isAvailBellCurveError && (
        <div className="mt-6 bg-surface-info px-6 py-4">
          <Text size="lg">
            {t('messages.error.noAvailError')}
            <Link url={bookURL ?? ''} data-testid="reservationURL">
              {t('buttonsAndLinks.clickHere')}
            </Link>
            .
          </Text>
        </div>
      )}
      {isAvailBellCurveError && (
        <div className="mt-6 bg-surface-info px-6 py-4">
          <Text size="lg" {...{ role: 'alert', 'aria-live': 'polite', 'aria-atomic': 'true' }}>
            <Trans i18nKey="messages.info.availBellCurve" components={[visitBookingPageLink]} />
          </Text>
        </div>
      )}
    </div>
  );
};

export default RoomsRates;
